import { useStore } from '@nanostores/react';
import { Root, Trigger, Content, Item, Arrow } from '@radix-ui/react-dropdown-menu';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { IconButton } from '~/components/ui/IconButton';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';

interface LivePreviewDropdownProps {
  openPreviewInSeparateTab: () => void;
  iframeUrl?: string;
}

export const LivePreviewDropdown = ({ iframeUrl, openPreviewInSeparateTab }: LivePreviewDropdownProps) => {
  const previewUrl = useStore(workbenchStore.previewsStore.previewUrl);
  const [isPreviewDropdownVisible, setIsPreviewDropdownVisible] = useState(false);

  const tunnelPassword = useStore(workbenchStore.previewsStore.tunnelPassword);

  const onTogglePreviewDropdown = useCallback((visible?: boolean) => {
    setIsPreviewDropdownVisible((value) => visible ?? !value);
  }, []);

  const onClosePreviewDropdown = useCallback(() => {
    setIsPreviewDropdownVisible(false);
  }, []);

  const copyPasswordToClipboard = useCallback(() => {
    const password = workbenchStore.previewsStore.getTunnelPassword();

    if (password) {
      navigator.clipboard.writeText(password).then(() => {
        toast.success('Password copied', {
          position: 'bottom-right',
          autoClose: 2000,
          hideProgressBar: true,
        });
      });
    }
  }, []);

  const dropdownItems = useMemo(() => {
    const items: Array<{ startIcon: string; handleSelect: () => void; mainContext?: string; rightIcon?: string }> = [
      {
        startIcon: 'i-heroicons:arrow-top-right-on-square',
        handleSelect: openPreviewInSeparateTab,
        mainContext: previewUrl,
      },
    ];

    if (tunnelPassword) {
      items.push({
        startIcon: 'i-heroicons:key',
        handleSelect: copyPasswordToClipboard,
        mainContext: '•'.repeat(16),
        rightIcon: 'i-heroicons:clipboard-document inline-block ml-auto',
      });
    }

    return items.map((item, index) => (
      <DropdownItem
        key={index}
        startIcon={item.startIcon}
        handleSelect={item.handleSelect}
        mainContext={item.mainContext}
        rightIcon={item.rightIcon}
      />
    ));
  }, [openPreviewInSeparateTab, copyPasswordToClipboard, previewUrl, tunnelPassword]);

  return (
    <Root open={isPreviewDropdownVisible} modal={false} onOpenChange={onTogglePreviewDropdown}>
      <Trigger disabled={!iframeUrl} asChild>
        <IconButton
          className="text-bolt-elements-textSecondary enabled:hover:text-bolt-elements-textPrimary"
          title="Live preview"
          variant="ghost"
          size="sm"
          icon="i-bolt:tab-move !size-[14px]"
        />
      </Trigger>

      <Content
        className="bg-bolt-elements-background-depth-1 rounded-lg shadow z-dialog pt-2.5 pb-3 px-2"
        asChild
        avoidCollisions={false}
        sideOffset={6}
        alignOffset={-19}
        align="end"
        onFocusOutside={onClosePreviewDropdown}
      >
        <div className="border border-bolt-elements-borderColor">
          {dropdownItems}

          <p className="max-w-[290px] mx-3 my-1 text-xs text-bolt-elements-textSecondary">
            Anyone with this link {tunnelPassword && '& password '} can see your preview until you leave the project.
          </p>

          <Arrow className="fill-bolt-elements-borderColor" width={14} height={7} />
        </div>
      </Content>
    </Root>
  );
};

interface DropdownItemProps {
  handleSelect: () => void;
  startIcon?: string;
  mainContext?: string;
  rightIcon?: string;
}

export const DropdownItem = ({ handleSelect, mainContext, startIcon, rightIcon }: DropdownItemProps) => (
  <Item
    className="flex gap-2.5 text-bolt-elements-textPrimary hover:bg-bolt-elements-item-backgroundActive rounded-lg px-2 py-1.5 cursor-pointer focus-visible:ring-0 focus-visible:outline-none"
    onSelect={handleSelect}
  >
    {startIcon && <span className={classNames('text-bolt-elements-textSecondary inline-block', startIcon)} />}
    <span className="text-xs mt-0.5">{mainContext}</span>
    {rightIcon && <span className={rightIcon} />}
  </Item>
);
